import React, { createContext, useContext, useState, useCallback, useEffect, useRef } from "react";
import type { CardConfig, AvatarVariant, AttemptCounter, ConsentState, Orientation, PostalAddress } from "./types";
import { MOCK_AVATAR_VARIANTS } from "./mock-data";
import { avatarService } from "@/services";
import { toast } from "sonner";

interface WizardContextValue {
  // Consent
  consent: ConsentState;
  setConsent: React.Dispatch<React.SetStateAction<ConsentState>>;

  // Card config
  config: CardConfig;
  setConfig: React.Dispatch<React.SetStateAction<CardConfig>>;

  // Generated avatars
  avatarVariants: AvatarVariant[];
  setAvatarVariants: React.Dispatch<React.SetStateAction<AvatarVariant[]>>;
  selectedAvatarId: string | null;
  setSelectedAvatarId: React.Dispatch<React.SetStateAction<string | null>>;

  // Attempt counter
  attempts: AttemptCounter;
  setAttempts: React.Dispatch<React.SetStateAction<AttemptCounter>>;

  // Card editor
  orientation: Orientation;
  setOrientation: React.Dispatch<React.SetStateAction<Orientation>>;
  avatarPosition: { x: number; y: number };
  setAvatarPosition: React.Dispatch<React.SetStateAction<{ x: number; y: number }>>;
  avatarScale: number;
  setAvatarScale: React.Dispatch<React.SetStateAction<number>>;

  // Submission
  firstName: string;
  setFirstName: React.Dispatch<React.SetStateAction<string>>;
  lastName: string;
  setLastName: React.Dispatch<React.SetStateAction<string>>;
  panLast5: string;
  setPanLast5: React.Dispatch<React.SetStateAction<string>>;
  deliveryType: "branch" | "postal";
  setDeliveryType: React.Dispatch<React.SetStateAction<"branch" | "postal">>;
  branchId: string | null;
  setBranchId: React.Dispatch<React.SetStateAction<string | null>>;
  postalAddress: PostalAddress;
  setPostalAddress: React.Dispatch<React.SetStateAction<PostalAddress>>;

  // Funding
  fundingAmountCents: number | null;
  setFundingAmountCents: React.Dispatch<React.SetStateAction<number | null>>;

  // Card background (tenant-managed artwork behind the avatar; null = none)
  backgroundId: string | null;
  setBackgroundId: React.Dispatch<React.SetStateAction<string | null>>;
  backgroundUrl: string | null;
  setBackgroundUrl: React.Dispatch<React.SetStateAction<string | null>>;

  // Photo-only mode
  usePhotoOnly: boolean;
  setUsePhotoOnly: React.Dispatch<React.SetStateAction<boolean>>;

  // Cutout (background-removed) avatars
  cutoutUrls: Record<string, string>;
  setCutoutUrls: React.Dispatch<React.SetStateAction<Record<string, string>>>;
  useCutout: boolean;
  setUseCutout: React.Dispatch<React.SetStateAction<boolean>>;
  processingBgIds: Set<string>;

  // Generate
  handleGenerate: (tenantId: string) => Promise<void>;
  generating: boolean;

  // Reset
  resetWizard: () => void;
}

const WizardContext = createContext<WizardContextValue>(null!);

export const useWizard = () => useContext(WizardContext);

const initialConsent: ConsentState = {
  privacyConsentedAt: null,
  termsAcceptedAt: null,
  photoTipsViewedAt: null,
};

const initialConfig: CardConfig = {
  color: null,
  style: null,
  styleSubChoice: null,
  hobby: null,
  selfieFile: null,
};

const initialAddress: PostalAddress = {
  line1: "",
  line2: "",
  city: "",
  state: "",
  postalCode: "",
  country: "United States",
};

const WIZARD_STORAGE_KEY = "cardmoji_wizard_state";

function loadPersistedState() {
  try {
    // Use localStorage (shared across tabs) so state survives Stripe redirects opening new tabs
    const raw = localStorage.getItem(WIZARD_STORAGE_KEY);
    if (raw) return JSON.parse(raw);
  } catch {}
  return null;
}

export const WizardProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const saved = useRef(loadPersistedState());
  const s = saved.current;

  const [consent, setConsent] = useState<ConsentState>(s?.consent || initialConsent);
  const [config, setConfig] = useState<CardConfig>(s?.config || initialConfig);
  const [avatarVariants, setAvatarVariants] = useState<AvatarVariant[]>(s?.avatarVariants || []);
  const [selectedAvatarId, setSelectedAvatarId] = useState<string | null>(s?.selectedAvatarId || null);
  const [attempts, setAttempts] = useState<AttemptCounter>(s?.attempts || { attemptsUsed: 0, attemptsFree: 5, overageChargedAmount: 0 });
  const [orientation, setOrientation] = useState<Orientation>(s?.orientation || "portrait");
  const [avatarPosition, setAvatarPosition] = useState(s?.avatarPosition || { x: 50, y: 50 });
  const [avatarScale, setAvatarScale] = useState(s?.avatarScale ?? 1);
  const [firstName, setFirstName] = useState(s?.firstName || "");
  const [lastName, setLastName] = useState(s?.lastName || "");
  const [panLast5, setPanLast5] = useState(s?.panLast5 || "");
  const [deliveryType, setDeliveryType] = useState<"branch" | "postal">(s?.deliveryType || "branch");
  const [branchId, setBranchId] = useState<string | null>(s?.branchId || null);
  const [postalAddress, setPostalAddress] = useState<PostalAddress>(s?.postalAddress || initialAddress);
  const [usePhotoOnly, setUsePhotoOnly] = useState(s?.usePhotoOnly || false);
  const [generating, setGenerating] = useState(false);
  const [cutoutUrls, setCutoutUrls] = useState<Record<string, string>>(s?.cutoutUrls || {});
  const [useCutout, setUseCutout] = useState(s?.useCutout ?? true);
  const [processingBgIds, setProcessingBgIds] = useState<Set<string>>(new Set());
  const [fundingAmountCents, setFundingAmountCents] = useState<number | null>(s?.fundingAmountCents ?? null);
  const [backgroundId, setBackgroundId] = useState<string | null>(s?.backgroundId ?? null);
  const [backgroundUrl, setBackgroundUrl] = useState<string | null>(s?.backgroundUrl ?? null);

  // Persist wizard state to sessionStorage on every change
  useEffect(() => {
    const state = {
      consent, config: { ...config, selfieFile: null }, // Files can't be serialized
      avatarVariants, selectedAvatarId, attempts,
      orientation, avatarPosition, avatarScale,
      firstName, lastName, panLast5,
      deliveryType, branchId, postalAddress,
      usePhotoOnly, cutoutUrls, useCutout, fundingAmountCents,
      backgroundId, backgroundUrl,
    };
    try { localStorage.setItem(WIZARD_STORAGE_KEY, JSON.stringify(state)); } catch {}
  }, [consent, config, avatarVariants, selectedAvatarId, attempts,
      orientation, avatarPosition, avatarScale,
      firstName, lastName, panLast5,
      deliveryType, branchId, postalAddress,
      usePhotoOnly, cutoutUrls, useCutout, fundingAmountCents,
      backgroundId, backgroundUrl]);

  const fileToBase64 = (file: File): Promise<string> =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        const result = reader.result as string;
        // Strip the data URL prefix to get just base64
        resolve(result);
      };
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });

  const fetchCompletedGenerations = useCallback(async (generationIds: string[], onPollComplete?: () => void) => {
    const { data, error } = await avatarService.pollGenerations(generationIds);

    if (error || !data?.generations) {
      console.error("Poll error:", error || data);
      toast.error("Failed to retrieve generated avatars");
      return;
    }

    const generations = data.generations as Array<{
      id: string;
      status: string;
      generated_image_url: string | null;
      error_message: string | null;
      variant_number: number;
    }>;

    const completed = generations
      .filter((g) => g.status === "completed" && g.generated_image_url)
      .sort((a, b) => a.variant_number - b.variant_number);

    if (completed.length > 0) {
      // Mark these variants as processing background removal
      const completedIds = completed.map((g) => g.id);
      setProcessingBgIds((prev) => new Set([...prev, ...completedIds]));

      // Show variants with placeholder (imageUrl empty until cutout ready)
      const newVariants = completed.map((g) => ({
        id: g.id,
        jobId: g.id,
        variantNumber: g.variant_number,
        imageUrl: "", // Empty until cutout is ready
      }));

      setAvatarVariants((prev) => {
        const existingIds = new Set(prev.map((v) => v.id));
        const fresh = newVariants.filter((v) => !existingIds.has(v.id));
        return [...fresh, ...prev]; // newest first
      });

      setSelectedAvatarId(newVariants[0].id);
      setAttempts((prev) => ({ ...prev, attemptsUsed: prev.attemptsUsed + 1 }));
      toast.success(`Generated ${completed.length} avatar variant${completed.length > 1 ? "s" : ""}!`);

      // Signal generation complete before starting background removal
      onPollComplete?.();

      // Process background removal in parallel
      await Promise.all(
        completed.map(async (g) => {
          try {
            // ONE shared cutout routine — same call the consumer funnel uses.
            const url = await avatarService.cutoutOrOriginal(g.id, g.generated_image_url!);
            if (url !== g.generated_image_url) {
              setCutoutUrls((prev) => ({ ...prev, [g.id]: url }));
            }
            setAvatarVariants((prev) =>
              prev.map((v) => v.id === g.id ? { ...v, imageUrl: url } : v)
            );
          } finally {
            setProcessingBgIds((prev) => {
              const next = new Set(prev);
              next.delete(g.id);

              return next;
            });
          }
        })
      );
    } else {
      const firstError = generations.find((g) => g.error_message)?.error_message || "Generation failed";
      toast.error(firstError);
    }
  }, []);

  const handleGenerate = useCallback(async (tenantId: string) => {
    if (!config.selfieFile || !config.style) {
      toast.error("Please upload a selfie and select a style first");
      return;
    }

    setGenerating(true);
    // Don't clear previous variants — we accumulate them
    setSelectedAvatarId(null);
    try {
      // Step 1: Get a signed upload URL from the edge function (server issues the path)
      const ext = config.selfieFile.name.split('.').pop() || 'jpg';

      console.log("Requesting signed upload URL");
      const { data: uploadData, error: uploadError } = await avatarService.getUploadUrl(ext);

      console.log("Upload URL response:", { uploadData, uploadError });

      const uploadResult = uploadData || (uploadError as any);
      if (!uploadResult?.signedUrl || !uploadResult?.path) {
        console.error("Failed to get upload URL:", uploadError || uploadData);
        toast.error(uploadResult?.error || "Failed to prepare upload");
        setGenerating(false);
        return;
      }
      const selfieFilename = uploadResult.path as string;

      // Step 2: Upload the selfie directly to storage using the signed URL
      const uploadRes = await fetch(uploadResult.signedUrl, {
        method: "PUT",
        headers: { "Content-Type": config.selfieFile.type },
        body: config.selfieFile,
      });

      if (!uploadRes.ok) {
        const errText = await uploadRes.text();
        console.error("Selfie upload failed:", errText);
        toast.error("Failed to upload selfie");
        setGenerating(false);
        return;
      }

      // Step 2b (B4): bind biometric consent to THIS selfie before generation.
      // The server-side gate refuses generation unless a consent row exists whose
      // meta->>'selfie_path' matches the storage path we're about to process.
      // For authenticated users this RPC also syncs consumer_profiles.selfie_storage_path.
      const { error: attachErr } = await avatarService.attachSelfieToUser(selfieFilename);
      if (attachErr) {
        console.error("Consent binding failed:", attachErr);
        toast.error(attachErr.message || "Please confirm biometric consent to continue");
        setGenerating(false);
        return;
      }

      // Step 3: Start generation with the storage path
      const { data, error } = await avatarService.startGeneration({
        tenantId,
        styleId: config.style,
        subStyleId: config.styleSubChoice || null,
        themeId: config.hobby || null,
        selfieStoragePath: selfieFilename,
        numVariants: 2,
      });

      if (error) {
        console.error("Generate error:", error);
        toast.error(error.message || "Failed to start generation");
        setGenerating(false);
        return;
      }

      if (data?.error) {
        toast.error(data.error);
        setGenerating(false);
        return;
      }

      if (data?.generationIds?.length) {
        toast.info("Generating avatars — this takes 30–60 seconds...");
        await fetchCompletedGenerations(data.generationIds, () => setGenerating(false));
      } else {
        toast.error("Failed to start avatar generation.");
        setGenerating(false);
      }
    } catch (err) {
      console.error("Generate error:", err);
      toast.error("Something went wrong generating avatars");
      setGenerating(false);
    }
  }, [config.selfieFile, config.style, config.styleSubChoice, config.hobby, fetchCompletedGenerations]);

  const resetWizard = () => {
    setConsent(initialConsent);
    setConfig(initialConfig);
    setAvatarVariants([]);
    setSelectedAvatarId(null);
    setAttempts({ attemptsUsed: 0, attemptsFree: 5, overageChargedAmount: 0 });
    setOrientation("landscape");
    setAvatarPosition({ x: 50, y: 50 });
    setAvatarScale(1);
    setFirstName("");
    setLastName("");
    setPanLast5("");
    setDeliveryType("branch");
    setBranchId(null);
    setPostalAddress(initialAddress);
    setUsePhotoOnly(false);
    setCutoutUrls({});
    setUseCutout(false);
    setFundingAmountCents(null);
    setBackgroundId(null);
    setBackgroundUrl(null);
    try { localStorage.removeItem(WIZARD_STORAGE_KEY); } catch {}
  };

  return (
    <WizardContext.Provider
      value={{
        consent, setConsent,
        config, setConfig,
        avatarVariants, setAvatarVariants,
        selectedAvatarId, setSelectedAvatarId,
        attempts, setAttempts,
        orientation, setOrientation,
        avatarPosition, setAvatarPosition,
        avatarScale, setAvatarScale,
        firstName, setFirstName,
        lastName, setLastName,
        panLast5, setPanLast5,
        deliveryType, setDeliveryType,
        branchId, setBranchId,
        postalAddress, setPostalAddress,
        fundingAmountCents, setFundingAmountCents,
        backgroundId, setBackgroundId,
        backgroundUrl, setBackgroundUrl,
        usePhotoOnly, setUsePhotoOnly,
        cutoutUrls, setCutoutUrls,
        useCutout, setUseCutout,
        processingBgIds,
        handleGenerate,
        generating,
        resetWizard,
      }}
    >
      {children}
    </WizardContext.Provider>
  );
};
